/**
 * Настройки профиля потока: из параметров индикатора в понятный слою вид.
 *
 * Параметры индикатора — плоский словарь строк и чисел, так их хранит
 * профиль человека. Слою нужна форма: множество бирж, доля зоны
 * стоимости дробью, отрезок словами. Перевод живёт здесь один раз.
 */

import type { OrderFlowVenue } from '@specter/types';
import type { IndicatorParams } from '../indicators/types.js';

/**
 * Что показывает профиль.
 *
 * `volume` — сколько всего наторговали на уровне.
 * `bidask` — продажи влево, покупки вправо от оси.
 * `delta`  — перевес: покупки минус продажи.
 */
export type FlowProfileMode = 'volume' | 'bidask' | 'delta';

/**
 * За какой отрезок собирать профиль.
 *
 * Таймфрейм свечи и отрезок профиля — разные вещи: на пятиминутках
 * удобно смотреть профиль за сутки, а на часовике — за видимую часть.
 */
export type FlowRange = 'visible' | 'day' | '1H' | '4H' | '24H' | 'bars';

export interface FlowProfileSettings {
  readonly mode: FlowProfileMode;
  readonly range: FlowRange;
  /** Сколько последних свечей — для отрезка `bars`. */
  readonly bars: number;
  readonly venues: ReadonlySet<OrderFlowVenue>;
  /** Доля объёма в зоне стоимости, 0…1. */
  readonly valueArea: number;
  readonly showPoc: boolean;
  readonly pocHistory: boolean;
  readonly showValueArea: boolean;
  /** Ширина профиля — доля ширины графика. */
  readonly width: number;
  /** У какого края графика стоит профиль. */
  readonly side: 'right' | 'left';
  readonly unit: 'usd' | 'coin';
  readonly colors: {
    readonly buy: string;
    readonly sell: string;
    readonly volume: string;
    readonly poc: string;
    readonly value: string;
  };
}

function text(p: IndicatorParams, key: string, fallback: string): string {
  const value = p[key];
  return typeof value === 'string' && value !== '' ? value : fallback;
}

function number(p: IndicatorParams, key: string, fallback: number): number {
  const value = p[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function flag(p: IndicatorParams, key: string, fallback: boolean): boolean {
  const value = p[key];
  return typeof value === 'boolean' ? value : fallback;
}

const MODES: readonly FlowProfileMode[] = ['volume', 'bidask', 'delta'];
const RANGES: readonly FlowRange[] = ['visible', 'day', '1H', '4H', '24H', 'bars'];

export function flowSettings(p: IndicatorParams): FlowProfileSettings {
  const mode = text(p, 'mode', 'volume') as FlowProfileMode;
  const range = text(p, 'range', 'visible') as FlowRange;
  const source = text(p, 'source', 'all');

  return {
    mode: MODES.includes(mode) ? mode : 'volume',
    range: RANGES.includes(range) ? range : 'visible',
    bars: Math.max(1, Math.round(number(p, 'bars', 100))),
    venues: new Set<OrderFlowVenue>(
      source === 'binance' ? ['binance'] : source === 'bybit' ? ['bybit'] : ['binance', 'bybit'],
    ),
    valueArea: Math.min(1, Math.max(0.05, number(p, 'valueArea', 70) / 100)),
    showPoc: flag(p, 'poc', true),
    pocHistory: flag(p, 'pocHistory', false),
    showValueArea: flag(p, 'valueLines', true),
    width: Math.min(0.6, Math.max(0.05, number(p, 'width', 25) / 100)),
    side: text(p, 'side', 'right') === 'left' ? 'left' : 'right',
    unit: text(p, 'unit', 'usd') === 'coin' ? 'coin' : 'usd',
    colors: {
      buy: text(p, 'buyColor', '#16C784'),
      sell: text(p, 'sellColor', '#EA3943'),
      volume: text(p, 'volumeColor', '#7C8DB5'),
      poc: text(p, 'pocColor', '#F5A623'),
      value: text(p, 'valueColor', '#5B8DEF'),
    },
  };
}

export const VENUE_TITLE: Record<OrderFlowVenue, string> = {
  binance: 'Binance',
  bybit: 'Bybit',
};

/** «$18,42 млн», «$840 тыс.», «$96». */
export function money(value: number): string {
  const sign = value < 0 ? '−' : '';
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2).replace('.', ',')} млрд`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2).replace('.', ',')} млн`;
  if (abs >= 1e3) return `${sign}$${Math.round(abs / 1e3)} тыс.`;
  return `${sign}$${Math.round(abs)}`;
}

/** Деньги со знаком плюс для перевеса: «+$3,24 млн». */
export function signedMoney(value: number): string {
  return value > 0 ? `+${money(value)}` : money(value);
}

/** Объём в монетах по цене уровня: «12,4 BTC». */
export function coins(usd: number, price: number, symbol: string): string {
  const base = symbol.split('/')[0] ?? '';
  const amount = price > 0 ? usd / price : 0;
  const digits = amount >= 100 ? 0 : amount >= 1 ? 2 : 4;
  return `${amount.toFixed(digits).replace('.', ',')} ${base}`;
}

/** Цена уровня так, как её пишет биржа: без лишних нулей. */
export function priceText(price: number, step: number): string {
  const digits = Math.max(0, Math.min(8, Math.ceil(-Math.log10(step)) + (step < 1 ? 1 : 0)));
  return price.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: digits });
}
